import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Chip,
  Grid,
  Button
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Refresh as NewAnalysisIcon,
  Download as DownloadIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon
} from '@mui/icons-material';
import { brandColors } from '../../theme/theme';
import ExecutiveSummaryCard from './ExecutiveSummaryCard';
import MetricsVisualization from './MetricsVisualization';
import ComparableFilmsComparison from './ComparableFilmsComparison';
import HighlightedText from './HighlightedText';

/**
 * LoglineAnalysisResults Component
 * 
 * Displays comprehensive logline analysis results in a professional,
 * tabbed layout with visual score gauge and organized sections.
 */

const LoglineAnalysisResults = ({ data, onNewAnalysis }) => {
  const [currentTab, setCurrentTab] = useState(0);

  // Extract score from data (fallback to parsing from text if needed)
  const overallScore = data.overall_score || extractScoreFromText(data.sections?.overall_assessment || '');
  const recommendation = data.recommendation || extractRecommendationFromText(data.sections?.overall_assessment || '');

  // Get score color
  const getScoreColor = (score) => {
    if (score >= 70) return '#388e3c'; // Green
    if (score >= 50) return '#f57c00'; // Orange
    return '#d32f2f'; // Red
  };

  const scoreColor = getScoreColor(overallScore);

  // Parse comparable films from sections (memoized for performance)
  const comparableFilms = useMemo(
    () => parseComparableFilms(data.sections?.comparable_films || ''),
    [data.sections?.comparable_films]
  );
  
  // Parse strengths and weaknesses
  const strengths = useMemo(
    () => parseListItems(data.sections?.strengths || ''),
    [data.sections?.strengths]
  );
  const weaknesses = useMemo(
    () => parseListItems(data.sections?.weaknesses_and_concerns || ''),
    [data.sections?.weaknesses_and_concerns]
  );
  
  // Parse recommendations
  const recommendations = useMemo(
    () => parseListItems(data.sections?.development_recommendations || ''),
    [data.sections?.development_recommendations]
  );

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      {/* Executive Summary Card - NEW */}
      <ExecutiveSummaryCard
        data={data}
        scoreColor={scoreColor}
      />

      {/* Hero Card - Overall Score */}
      <Paper
        elevation={3}
        sx={{
          p: 4,
          mb: 4,
          textAlign: 'center',
          background: `linear-gradient(135deg, ${brandColors.white} 0%, ${brandColors.primary}05 100%)`,
          borderTop: `4px solid ${scoreColor}`
        }}
      >
        <Typography variant="h4" sx={{ color: brandColors.primary, fontWeight: 600, mb: 3 }}>
          Analysis Complete
        </Typography>

        {/* Score Gauge */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              width: 150,
              height: 150,
              borderRadius: '50%',
              border: `8px solid ${scoreColor}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
              position: 'relative',
              background: `${scoreColor}10`
            }}
          >
            <Typography
              variant="h2"
              sx={{
                color: scoreColor,
                fontWeight: 700
              }}
            >
              {overallScore}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: brandColors.primary, opacity: 0.7 }}>
            Overall Score (out of 100)
          </Typography>
        </Box>

        {/* Recommendation Badge */}
        <Chip
          icon={overallScore >= 70 ? <TrendingUpIcon /> : <TrendingDownIcon />}
          label={recommendation || 'See Full Analysis'}
          sx={{
            fontSize: '1rem',
            py: 2.5,
            px: 2,
            backgroundColor: `${scoreColor}20`,
            color: scoreColor,
            fontWeight: 600,
            '& .MuiChip-icon': {
              color: scoreColor
            }
          }}
        />

        {/* Actions */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<NewAnalysisIcon />}
            onClick={onNewAnalysis}
            sx={{
              borderColor: brandColors.primary,
              color: brandColors.primary,
              '&:hover': {
                borderColor: brandColors.secondary,
                backgroundColor: `${brandColors.secondary}10`
              }
            }}
          >
            New Analysis
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            sx={{
              backgroundColor: brandColors.secondary,
              '&:hover': {
                backgroundColor: brandColors.primary
              }
            }}
            onClick={() => alert('PDF export coming in Phase 5!')}
          >
            Download PDF
          </Button>
        </Box>
      </Paper>

      {/* Tabbed Content */}
      <Paper elevation={2}>
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 500
            },
            '& .Mui-selected': {
              color: brandColors.secondary
            },
            '& .MuiTabs-indicator': {
              backgroundColor: brandColors.secondary
            }
          }}
        >
          <Tab label="Executive Summary" />
          <Tab label="Overview" />
          <Tab label="Story" />
          <Tab label="Market" />
          <Tab label="Insights" />
          <Tab label="Recommendations" />
          <Tab label="Verdict" />
        </Tabs>

        {/* Tab Panels */}
        <Box sx={{ p: 4 }}>
          {/* Tab 0: Executive Summary */}
          {currentTab === 0 && (
            <Box>
              <Typography variant="h5" sx={{ color: brandColors.primary, fontWeight: 600, mb: 3 }}>
                Quick Analysis Overview
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <HighlightedText
                    content={data.sections?.overall_assessment}
                    title="Overall Assessment"
                    maxLength={400}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <HighlightedText
                    content={data.sections?.concept_analysis?.substring(0, 800)}
                    title="Concept Highlights"
                    maxLength={400}
                  />
                </Grid>
              </Grid>

              {/* Key Metrics */}
              <MetricsVisualization
                distributionText={data.sections?.distribution_strategy || ''}
                marketText={data.sections?.market_analysis || ''}
              />
            </Box>
          )}

          {/* Tab 1: Overview */}
          {currentTab === 1 && (
            <Box>
              <HighlightedText content={data.sections?.overall_assessment} title="Overall Assessment" />
              <HighlightedText content={data.sections?.concept_analysis} title="Concept Analysis" />
            </Box>
          )}

          {/* Tab 2: Story */}
          {currentTab === 2 && (
            <Box>
              <HighlightedText content={data.sections?.story_elements} title="Story Elements" maxLength={800} />
              
              {/* Enhanced Comparable Films Component with Chart Toggle */}
              {comparableFilms.length > 0 && (
                <ComparableFilmsComparison films={comparableFilms} />
              )}
            </Box>
          )}

          {/* Tab 3: Market */}
          {currentTab === 3 && (
            <Box>
              <HighlightedText content={data.sections?.market_analysis} title="Market Analysis" />
              
              {/* Financial Metrics Visualization */}
              <MetricsVisualization
                distributionText={data.sections?.distribution_strategy || ''}
                marketText={data.sections?.market_analysis || ''}
              />
              
              <HighlightedText content={data.sections?.distribution_strategy} title="Distribution Strategy" />
            </Box>
          )}

          {/* Tab 4: Insights */}
          {currentTab === 4 && (
            <Box>
              <InsightsList 
                title="Strengths" 
                items={strengths}
                icon={<CheckIcon />}
                color="#388e3c"
              />
              <InsightsList 
                title="Weaknesses & Concerns" 
                items={weaknesses}
                icon={<WarningIcon />}
                color="#f57c00"
              />
            </Box>
          )}

          {/* Tab 5: Recommendations */}
          {currentTab === 5 && (
            <Box>
              <RecommendationsList items={recommendations} />
              <HighlightedText content={data.sections?.talent_considerations} title="Talent Considerations" />
            </Box>
          )}

          {/* Tab 6: Verdict */}
          {currentTab === 6 && (
            <Box>
              <Paper
                elevation={1}
                sx={{
                  p: 3,
                  mb: 3,
                  borderLeft: `6px solid ${scoreColor}`,
                  backgroundColor: `${scoreColor}05`
                }}
              >
                <Typography variant="h5" sx={{ color: brandColors.primary, fontWeight: 600, mb: 2 }}>
                  Final Verdict
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Chip
                    label={`Score: ${overallScore}/100`}
                    sx={{
                      backgroundColor: scoreColor,
                      color: brandColors.white,
                      fontWeight: 600,
                      fontSize: '1.1rem'
                    }}
                  />
                  <Chip
                    label={recommendation}
                    variant="outlined"
                    sx={{
                      borderColor: scoreColor,
                      color: scoreColor,
                      fontWeight: 600,
                      fontSize: '1rem'
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    color: brandColors.primary,
                    opacity: 0.85,
                    lineHeight: 1.8,
                    fontSize: '1.05rem',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {cleanText(data.sections?.final_verdict || '')}
                </Typography>
              </Paper>
              
              {/* Full Analysis Text */}
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" sx={{ color: brandColors.primary, fontWeight: 600, mb: 2 }}>
                  Complete Analysis ({data.word_count?.toLocaleString() || '0'} words)
                </Typography>
                <Typography
                  sx={{
                    color: brandColors.primary,
                    opacity: 0.7,
                    fontSize: '0.9rem',
                    mb: 2
                  }}
                >
                  The full, unabridged AI analysis below contains all insights consolidated.
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    backgroundColor: '#f5f5f5',
                    border: `1px solid ${brandColors.primary}20`,
                    maxHeight: 600,
                    overflow: 'auto'
                  }}
                >
                  <Typography
                    sx={{
                      color: brandColors.primary,
                      opacity: 0.85,
                      lineHeight: 1.8,
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'monospace',
                      fontSize: '0.85rem'
                    }}
                  >
                    {data.full_analysis}
                  </Typography>
                </Paper>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

// Helper Components
const InsightsList = ({ title, items, icon, color }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" sx={{ color: brandColors.primary, fontWeight: 600, mb: 2 }}>
      {title}
    </Typography>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {items.map((item, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            gap: 2,
            p: 2,
            backgroundColor: `${color}10`,
            borderRadius: 1,
            borderLeft: `4px solid ${color}`
          }}
        >
          <Box sx={{ color: color, mt: 0.5 }}>
            {icon}
          </Box>
          <Typography sx={{ color: brandColors.primary, opacity: 0.85, flex: 1 }}>
            {item}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

const RecommendationsList = ({ items }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" sx={{ color: brandColors.primary, fontWeight: 600, mb: 2 }}>
      Development Recommendations
    </Typography>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {items.map((item, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            gap: 2,
            p: 2,
            backgroundColor: brandColors.white,
            border: `1px solid ${brandColors.primary}20`,
            borderRadius: 1
          }}
        >
          <Typography
            sx={{
              color: brandColors.secondary,
              fontWeight: 700,
              fontSize: '1.2rem',
              minWidth: 30
            }}
          >
            {index + 1}.
          </Typography>
          <Typography sx={{ color: brandColors.primary, opacity: 0.85, flex: 1 }}>
            {item}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

// Helper Functions
function extractScoreFromText(text) {
  const match = text.match(/Overall Score.*?(\d+)/i);
  return match ? parseInt(match[1]) : 50;
}

function extractRecommendationFromText(text) {
  const match = text.match(/Recommendation:\*\*\s*(.+?)(?:\n|\*)/i);
  return match ? match[1].trim() : 'See Full Analysis';
}

function cleanText(text) {
  return text
    .replace(/^[#*\s]+/, '') // Remove leading markdown
    // eslint-disable-next-line no-useless-escape
    .replace(/\*\*(.+?)\*\*/g, '$1') // Remove bold markers
    .replace(/^\d+\.\s+/, '') // Remove leading numbers
    .trim();
}

function parseComparableFilms(text) {
  const films = [];
  
  // Split by numbered items
  const filmSections = text.split(/\d+\.\s+\*\*/).filter(s => s.trim().length > 0);
  
  for (const section of filmSections) {
    // Extract title and year
    const titleMatch = section.match(/^(.+?)\*\*/);
    if (!titleMatch) continue;
    
    const fullTitle = titleMatch[1].trim();
    const yearMatch = fullTitle.match(/\((\d{4})\)/);
    const year = yearMatch ? yearMatch[1] : null;
    const title = fullTitle.replace(/\(\d{4}\)/, '').trim();
    
    // Extract connection
    const connectionMatch = section.match(/\*\*Connection:\*\*\s*(.+?)(?:\n|$|\*\*)/s);
    const connection = connectionMatch ? connectionMatch[1].trim() : '';
    
    // Extract box office (display and numeric value)
    const boxOfficeMatch = section.match(/Box Office[:\s]*\$?([\d.]+)([MB])/i);
    let boxOffice = null;
    let boxOfficeValue = null;
    
    if (boxOfficeMatch) {
      const value = parseFloat(boxOfficeMatch[1]);
      const unit = boxOfficeMatch[2].toUpperCase();
      boxOfficeValue = unit === 'B' ? value * 1000 : value; // Convert billions to millions
      boxOffice = `$${boxOfficeMatch[1]}${unit}`;
    }
    
    // Extract learning point
    const learningMatch = section.match(/\*\*Learn(?:ing)?(?:\s+Point)?:\*\*\s*(.+?)(?:\n|$)/s);
    const learning = learningMatch ? learningMatch[1].trim() : '';
    
    films.push({
      title,
      year,
      boxOffice,
      boxOfficeValue, // Numeric value for charts
      connection,
      learning
    });
  }
  
  return films.slice(0, 7); // Limit to 7 films
}

function parseListItems(text) {
  const items = [];
  const lines = text.split('\n');
  
  for (const line of lines) {
    const cleanLine = line.replace(/^[\d.*\s-]+/, '').trim();
    if (cleanLine && cleanLine.length > 10 && !cleanLine.startsWith('#')) {
      items.push(cleanLine);
    }
  }
  
  return items.slice(0, 10); // Limit to 10 items
}

export default LoglineAnalysisResults;

